import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import Header from '../../components/Header'
import { Box, Container, Typography, Stack, Grid, Paper, Chip, Divider, Button } from '@mui/material'
import { VerifiedUser, Download, CalendarMonth, LocalActivity, WarningAmber, ErrorOutline, WhatsApp } from '@mui/icons-material'
import ButtonPng from './ButtonPng'
import { getUserCarteirinhaByCpfPngApi } from '../../Api'
import LoadingUI from '../../components/LoadingUI'
import FooterHome from '../Home2/components/FooterHome'
import './styleMinhaCarteira.css'
import CardValidacaoDigitalUEN from './CardValidacaoDigitalUEN'
import { getWalletStatus } from './getWalletStatus'
import { getUENUser } from '../../utils'

export default function MinhaCarteira() {
    // Usa o helper centralizado (com try/catch) em vez de JSON.parse direto —
    // evita TypeError em caso de localStorage corrompido ou ausente.
    const state = getUENUser();
    const [imageReady, setImageReady] = useState(false);
    const [imagem, setImagem] = useState(null);
    const [loadingImage, setLoadingImage] = useState(false);

    const walletStatus = state ? getWalletStatus(state.ano) : null;

    // useEffect chamado sempre (rules of hooks); a guarda fica dentro dele.
    useEffect(() => {
        if (!state?.cpf) return;
        let blobUrl;
        setLoadingImage(true);
        getUserCarteirinhaByCpfPngApi({ cpf: state.cpf })
            .then(res => {
                blobUrl = URL.createObjectURL(res.data);
                setImagem(blobUrl);
            })
            .finally(() => setLoadingImage(false));

        return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
    }, [state?.cpf]);

    // Sem usuário no storage -> manda pro login. Tem que vir DEPOIS dos hooks
    // pra não violar rules-of-hooks.
    if (!state) return <Navigate to="/login" replace />;

    const handleRenovacaoZap = () => {
        const mensagem = `Olá! Minha carteira UEN (${state.ano}) está ${walletStatus === 'expired' ? 'vencida' : 'perto de vencer'} e gostaria de solicitar a renovação. Nome: ${state.nome}, CPF: ${state.cpf}`;
        const fone = "5531997333998";
        window.open(`https://api.whatsapp.com/send?phone=${fone}&text=${encodeURIComponent(mensagem)}`, '_blank');
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(180deg, #F8FAFC 0%, #ECFDF5 100%)',
            fontFamily: "'Inter', 'Roboto', sans-serif",
        }}>
            <Header />

            {loadingImage ? <LoadingUI /> : (
                <Container maxWidth="xl" sx={{ flex: 1, py: { xs: 4, md: 8 } }}>
                    <Grid container spacing={4} alignItems="center" justifyContent="center">

                        {/* LADO ESQUERDO: PREVIEW DA CARTEIRA */}
                        <Grid item xs={12} md={5} lg={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Paper elevation={24} className="wallet-canvas">
                                <div id='node_to_png202526'>
                                    <img
                                        src={imagem}
                                        crossOrigin="anonymous"
                                        onLoad={() => setImageReady(true)}
                                        alt="Sua Carteira"
                                        className="wallet-img-modern"
                                    />
                                </div>
                                <Box className="wallet-overlay-gloss" />
                            </Paper>
                        </Grid>

                        {/* LADO DIREITO: INFO E AÇÕES */}
                        <Grid item xs={12} md={7} lg={5}>
                            <Box className="info-panel">
                                <Stack spacing={3}>
                                    <Box>
                                        <Chip
                                            icon={walletStatus === 'active' ? <VerifiedUser /> : walletStatus === 'warning' ? <WarningAmber /> : <ErrorOutline />}
                                            label={walletStatus === 'active' ? "Documento Oficial Ativo" : walletStatus === 'warning' ? "Vencimento Próximo" : "Documento Expirado"}
                                            color={walletStatus === 'active' ? "success" : walletStatus === 'warning' ? "warning" : "error"}
                                            variant="outlined"
                                            sx={{ mb: 2, fontWeight: 600, borderRadius: '999px' }}
                                        />
                                        <Typography variant="h3" className="welcome-text">
                                            Olá, {state.nome.split(' ')[0]}!
                                        </Typography>
                                        <Typography variant="body1" color="textSecondary">
                                            Sua carteira digital de estudante <strong>{state.ano}</strong> está disponível.
                                        </Typography>
                                    </Box>

                                    {/* BLOCO DE RENOVAÇÃO (SÓ APARECE SE EXPIRADO OU WARNING) */}
                                    {(walletStatus === 'expired' || walletStatus === 'warning') && (
                                        <Paper sx={{
                                            p: 2,
                                            bgcolor: walletStatus === 'expired' ? '#FFF1F2' : '#FFFBEB',
                                            border: `1px solid ${walletStatus === 'expired' ? '#FECDD3' : '#FDE68A'}`,
                                            borderRadius: '14px',
                                            boxShadow: 'none',
                                        }}>
                                            <Typography variant="subtitle2" sx={{
                                                mb: 1, fontWeight: 700,
                                                color: walletStatus === 'expired' ? '#9F1239' : '#92400E',
                                            }}>
                                                {walletStatus === 'expired' ? 'Sua carteirinha venceu' : 'Renovação disponível'}
                                            </Typography>
                                            <Typography variant="caption" display="block" sx={{ mb: 2, color: '#475569' }}>
                                                Para garantir seus benefícios de meia-entrada no próximo período, solicite sua renovação agora.
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                startIcon={<WhatsApp />}
                                                onClick={handleRenovacaoZap}
                                                sx={{
                                                    bgcolor: '#10B981',
                                                    '&:hover': { bgcolor: '#059669' },
                                                    borderRadius: '10px',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    boxShadow: '0 4px 12px -2px rgba(16, 185, 129, 0.4)',
                                                }}
                                            >
                                                Renovar pelo WhatsApp
                                            </Button>
                                        </Paper>
                                    )}

                                    <Divider />

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Paper variant="outlined" className="mini-card">
                                                <CalendarMonth sx={{ color: '#059669' }} />
                                                <Typography variant="caption" display="block" sx={{ color: '#64748B' }}>Expira em</Typography>
                                                <Typography variant="subtitle2" sx={{ color: '#0F172A', fontWeight: 700 }}>31/03/{Number(state.ano) + 1}</Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Paper variant="outlined" className="mini-card">
                                                <LocalActivity sx={{ color: '#10B981' }} />
                                                <Typography variant="caption" display="block" sx={{ color: '#64748B' }}>Benefício</Typography>
                                                <Typography variant="subtitle2" sx={{ color: '#0F172A', fontWeight: 700 }}>Meia-Entrada</Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    <Box className="action-box">
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Download fontSize="small" /> Opções de Download
                                        </Typography>

                                        <ButtonPng
                                            disabled={!imageReady || walletStatus === 'expired'}
                                            cpf={state.cpf}
                                            nome={state.nome}
                                            textButton={walletStatus === 'expired' ? "Download Bloqueado" : "Exportar como PDF Oficial"}
                                            id='node_to_png202526'
                                        />
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>
                    </Grid>
                    
                    {/* Passamos o status para o componente de validação também desabilitar funções se necessário */}
                    <CardValidacaoDigitalUEN cpf={state.cpf} walletStatus={walletStatus} nome={state.nome} />
                </Container>
            )}
            <FooterHome />
        </Box>
    )
}